/**
 * One registry of Admin Portal screens and the permission each one needs.
 *
 * There used to be two lists that both decided where an operator may go — the
 * sidebar's NAVIGATION_ITEMS, which filtered on permissions, and the command
 * palette's own PAGES array, which did not. The palette therefore offered
 * "Create Tenant" to a Billing operator who cannot create tenants. A third
 * place, the router, checked only that the caller was signed in, so typing or
 * bookmarking a URL rendered any screen for any operator.
 *
 * This is NOT a second permission model. The role -> permission mapping has one
 * owner, backend/shared/constants/operator_permissions.py, and the operator's
 * effective set is whatever /api/admin/capabilities returns. What is recorded
 * here is which permission a *screen* needs, which is a property of the screen.
 *
 * Hiding a screen is a convenience, never the control: every underlying route
 * is enforced server-side by require_operator_permission, so a bookmarked URL
 * or a hand-made request is refused regardless of what the UI drew.
 */
import {
  Building2, Users, CreditCard, Boxes, Key, Rocket, Gauge, FileText,
  GitCompare, LayoutDashboard, ShieldCheck, Receipt, Plug,
} from 'lucide-react';

/**
 * SECTIONS ARE OPERATOR JOBS, NOT BACKEND COLLECTIONS.
 *
 * The previous five sections were named after where things live in the system
 * — "Platform Configuration" held the licence catalogue, the price plans, the
 * product list, the quota catalogue AND the release channel, which is four
 * different jobs done by three different people. An operator pricing a
 * quotation and an operator rolling out a release were looking in the same
 * drawer.
 *
 * Nothing was removed, nothing changed permission and no path moved. The only
 * addition is Connections, and it exists because it has a working screen behind
 * it — see the note on Connections below, and on `/integrations`, which does
 * not. `navigation.test.js` pins the whole inventory so a regrouping cannot
 * quietly drop a destination.
 *
 * THERE IS NO SETTINGS SECTION, DELIBERATELY. The target IA names one, and this
 * Admin has no platform-scope settings screen to put in it. An empty section is
 * a control that reads as present and does nothing, which is worse than an
 * absent one: an operator who finds it concludes the setting they want is not
 * configurable, rather than that the screen has not been built. Add the section
 * with its first real screen.
 */
export const NAVIGATION_ITEMS = [
  {
    section: 'Overview',
    items: [
      { name: 'Dashboard', path: '/', icon: LayoutDashboard, description: 'Control plane', permission: 'tenant:read' },
    ],
  },
  {
    section: 'Tenants',
    items: [
      { name: 'Tenants', path: '/tenants', icon: Building2, description: 'Manage organizations', permission: 'tenant:read' },
      { name: 'Users', path: '/users', icon: Users, description: 'Monitor all users', permission: 'user:read' },
    ],
  },
  {
    // What Cluvik sells and what a plan is allowed to contain — the catalogue
    // layer. Read together because changing a module usually means changing the
    // licence and the quota that go with it.
    section: 'Product',
    items: [
      { name: 'Products', path: '/modules', icon: Boxes, description: 'Sellable products', permission: 'module:read' },
      { name: 'License Catalog', path: '/license-catalog', icon: Key, description: 'License definitions', permission: 'license:read' },
      { name: 'Limits & Templates', path: '/quotas', icon: Gauge, description: 'Versioned tenant allowances', permission: 'quota:read' },
    ],
  },
  {
    // What a customer is charged. plan:read and billing:read are different
    // permissions and stay that way — a bundle and a quotation are the plan
    // layer's own objects, gated on plan:read/plan:write server-side.
    section: 'Commerce',
    items: [
      { name: 'Plans & Billing', path: '/subscriptions', icon: CreditCard, description: 'Subscription plans', permission: 'billing:read' },
      { name: 'Bundles & Quotations', path: '/commercial', icon: Receipt, description: 'Priced offers for customers', permission: 'plan:read' },
    ],
  },
  {
    // The control-plane view of external provider credentials. One screen so
    // far, and it is backed by a route that answers: GET
    // /api/admin/sample-connection-profiles returns 401 unauthenticated on the
    // serving build, which is what a real route does.
    //
    // `/integrations` is NOT here, and that is not an oversight — see
    // EXTRA_SCREENS.
    section: 'Connections',
    items: [
      { name: 'Connection Profiles', path: '/connections/profiles', icon: Plug, description: 'Shared provider credentials for non-Live tenants', permission: 'tenant:read' },
    ],
  },
  {
    section: 'Platform',
    items: [
      { name: 'Releases', path: '/releases', icon: Rocket, description: 'Platform versions', permission: 'release:read' },
      { name: 'Platform Drift', path: '/platform-drift', icon: GitCompare, description: 'Legacy vs canonical', permission: 'provisioning:read' },
    ],
  },
  {
    // Who did what, and who may sign in at all. operator:read is owner-only, so
    // Team Access is invisible to every other role without the sidebar needing
    // to know that.
    section: 'Governance',
    items: [
      { name: 'Audit Logs', path: '/audit-logs', icon: FileText, description: 'System activity', permission: 'audit:read' },
      // Label only. The route, the permission (`operator:read`) and the
      // `platform_operators` collection keep their names — renaming those would
      // be churn across the backend, the tests and the audit history for no
      // gain. "Operator" is control-plane jargon; the people using this screen
      // call them admin users.
      { name: 'Team Access', path: '/operators', icon: ShieldCheck, description: 'Who can sign into this portal', permission: 'operator:read' },
    ],
  },
];

export const ALL_NAV_ITEMS = NAVIGATION_ITEMS.flatMap((s) => s.items);

/**
 * Screens that are reachable but are not sidebar destinations — deep links and
 * actions. They still need a permission, and the palette still offers some of
 * them, so they belong in the same registry rather than in a second list.
 *
 * `permission` is the permission the screen's own work requires, which is not
 * always the permission needed to *read* the area: Create Tenant lives under
 * Tenants but needs tenant:create, so a Read-Only or Billing operator can list
 * tenants and still not reach the create form.
 */
export const EXTRA_SCREENS = [
  { name: 'Create Tenant', path: '/tenants/create', icon: Building2, permission: 'tenant:create' },
  { name: 'Tenant Detail', path: '/tenants/:tenantId', permission: 'tenant:read' },
  { name: 'Edit Tenant', path: '/tenants/:tenantId/edit', permission: 'tenant:update' },
  { name: 'Tenant Usage', path: '/tenants/:tenantId/usage', permission: 'usage:read' },
  { name: 'User Detail', path: '/users/:userId', permission: 'user:read' },
  // KEPT OUT OF THE SIDEBAR ON PURPOSE — this screen cannot load.
  //
  // It fetches /api/admin/integrations/categories and .../providers. Those
  // routes exist in neither repository: `modules/integrations/api/__init__.py`
  // in the Platform records that `admin_integration_routes` "left with the
  // Admin Portal split (62270cf)", and nothing in this repository's
  // `modules/admin/api/` defines them. Measured against the serving build with
  // a positive control, so this is not an auth artifact:
  //
  //     GET /api/admin/tenants                  -> 401  (route exists)
  //     GET /api/admin/integrations/categories  -> 404
  //     GET /api/admin/integrations/providers   -> 404
  //
  // Promoting it to a Connections destination would make a dead end look like
  // the answer to "where do I manage providers". It stays reachable by URL,
  // where an operator who lands on it is already looking for a specific thing,
  // until the routes are restored.
  { name: 'Integrations', path: '/integrations', permission: 'tenant:read' },
];

/** Every screen the portal can render, with the permission it requires. */
export const ALL_SCREENS = [...ALL_NAV_ITEMS, ...EXTRA_SCREENS];

/**
 * The permission a concrete pathname requires, or null if the path is not a
 * known screen. Longest match wins, so `/tenants/create` resolves to
 * tenant:create rather than to `/tenants`' tenant:read.
 */
export function permissionForPath(pathname) {
  let best = null;
  for (const screen of ALL_SCREENS) {
    // Compare segment by segment so `:param` segments match any single value
    // and `/tenants/create` never matches `/tenants/:tenantId` first.
    const want = screen.path.split('/').filter(Boolean);
    const got = pathname.split('/').filter(Boolean);
    if (want.length !== got.length) continue;
    const literalHits = want.reduce((n, seg, i) => {
      if (seg.startsWith(':')) return n;
      return seg === got[i] ? n + 1 : -Infinity;
    }, 0);
    if (literalHits < 0) continue;
    if (!best || literalHits > best.literalHits) {
      best = { literalHits, permission: screen.permission, name: screen.name };
    }
  }
  return best;
}
